/**
 * HomeStore — local-first. Reads from IndexedDB and computes all stats
 * (weekly totals, today totals, 7-day detail) on the client.
 */
import { atom } from 'jotai'
import type { createStore } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { atomFamily } from 'jotai/utils'
import { db, type LocalSession, type CustomExerciseTemplate, type LocalCustomExercise } from '@/lib/db'
import { PRESET_BY_KEY, type PresetCategory } from '@/lib/exerciseRegistry'
import { pageLinks } from '../pageLinks'

export type HomeGroupKey = PresetCategory | 'custom'

type Store = ReturnType<typeof createStore>

const BJ_OFFSET_MS = 8 * 60 * 60 * 1000
const ONE_DAY_MS = 24 * 60 * 60 * 1000
const DAY_NAMES = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']

export interface AnaerobicBreakdownItem {
  exerciseKey: string
  label: string
  template: CustomExerciseTemplate
  /** Aggregated metric per template:
   *  - countdown: total minutes
   *  - sets_with_rest: total sets
   *  - manual_reps: { sets, reps } */
  minutes?: number
  sets?: number
  reps?: number
}

interface AnaerobicDetail {
  items: AnaerobicBreakdownItem[]
}

interface WeekBounds {
  todayStartUtc: Date
  weekStartUtc: Date
  weekEndUtc: Date
  todayDateStr: string
  todayIndex: number
}

function getWeekBounds(): WeekBounds {
  const now = Date.now()
  const bj = new Date(now + BJ_OFFSET_MS)
  const y = bj.getUTCFullYear()
  const m = bj.getUTCMonth()
  const d = bj.getUTCDate()
  const dow = bj.getUTCDay() // 0=Sun
  const todayStartUtc = new Date(Date.UTC(y, m, d) - BJ_OFFSET_MS)
  const mondayOffset = dow === 0 ? -6 : 1 - dow
  const weekStartUtc = new Date(Date.UTC(y, m, d + mondayOffset) - BJ_OFFSET_MS)
  const weekEndUtc = new Date(weekStartUtc.getTime() + 7 * ONE_DAY_MS)
  const todayDateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  const todayIndex = dow === 0 ? 6 : dow - 1
  return { todayStartUtc, weekStartUtc, weekEndUtc, todayDateStr, todayIndex }
}

function calcAnaerobic(
  sessions: LocalSession[],
  customByKey: Map<string, LocalCustomExercise>,
): AnaerobicDetail {
  // Aggregate by exerciseKey
  const agg = new Map<string, { minutes: number; sets: number; reps: number }>()
  for (const s of sessions) {
    if (s.exerciseType !== 'anaerobic') continue
    const summary = s.summary ?? {}
    const cur = agg.get(s.exerciseKey) ?? { minutes: 0, sets: 0, reps: 0 }
    const tpl = customByKey.get(s.exerciseKey)?.template
    if (tpl === 'countdown') {
      cur.minutes += Math.round(s.durationSeconds / 60)
      cur.sets += Number(summary.totalSets ?? 0)
    } else if (tpl === 'manual_reps') {
      cur.sets += Number(summary.totalSets ?? 0)
      cur.reps += Number(summary.totalReps ?? 0)
    } else {
      // Unknown template (e.g. orphan history with no metadata) — fall back to minutes
      cur.minutes += Math.round(s.durationSeconds / 60)
    }
    agg.set(s.exerciseKey, cur)
  }

  const items: AnaerobicBreakdownItem[] = []
  for (const [exerciseKey, v] of agg) {
    const meta = customByKey.get(exerciseKey)
    if (!meta) continue   // orphan; skip from breakdown UI
    if (v.minutes === 0 && v.sets === 0 && v.reps === 0) continue
    items.push({
      exerciseKey,
      label: meta.name,
      template: meta.template,
      ...(v.minutes > 0 ? { minutes: v.minutes } : {}),
      ...(v.sets > 0 ? { sets: v.sets } : {}),
      ...(v.reps > 0 ? { reps: v.reps } : {}),
    })
  }
  return { items }
}

// ============================================================================
// Data loader — re-runs when query is invalidated
// ============================================================================

export const refreshAtom = atomWithQuery(() => ({
  queryKey: ['HomeStore'],
  queryFn: async () => {
    const bounds = getWeekBounds()
    const [allSessionsRaw, allExercises, allCustoms] = await Promise.all([
      db.sessions.toArray(),
      db.userExercises.toArray(),
      db.customExercises.toArray(),
    ])
    // Soft-deleted sessions vanish from every stats surface immediately.
    const allSessions = allSessionsRaw.filter(s => !s.pendingDelete)
    // Exclude exercises marked pendingDelete (they're being removed)
    const exercises = allExercises
      .filter(e => !e.pendingDelete)
      .sort((a, b) => a.sortOrder - b.sortOrder)

    const customByKey = new Map(allCustoms.filter(c => !c.pendingDelete).map(c => [c.exerciseKey, c]))
    const customTemplateByKey = new Map(allCustoms.map(c => [c.exerciseKey, c.template]))

    const weekStartIso = bounds.weekStartUtc.toISOString()
    const weekEndIso = bounds.weekEndUtc.toISOString()
    const weekSessions = allSessions.filter(s => s.startedAt >= weekStartIso && s.startedAt <= weekEndIso)

    // Weekly aggregation
    let aerobicMinutes = 0
    const weekAnaerobic: LocalSession[] = []
    for (const s of weekSessions) {
      if (s.exerciseType === 'aerobic') aerobicMinutes += Math.round(s.durationSeconds / 60)
      else weekAnaerobic.push(s)
    }

    // Today aggregation
    const todayStartIso = bounds.todayStartUtc.toISOString()
    const todaySessions = weekSessions.filter(s => s.startedAt >= todayStartIso)
    let todayAerobicMinutes = 0
    const todayAnaerobic: LocalSession[] = []
    for (const s of todaySessions) {
      if (s.exerciseType === 'aerobic') todayAerobicMinutes += Math.round(s.durationSeconds / 60)
      else todayAnaerobic.push(s)
    }

    // Resolve exercise metadata — all exercises now live in customExercises.
    // Each entry also carries a `groupKey` so the Home page can bucket exercises
    // into the same muscle-group sections used by the add-exercise picker.
    const exercisesWithMeta = exercises
      .map(e => {
        const custom = customByKey.get(e.exerciseKey)
        if (!custom) return null
        // Group: prefer the preset's category; for user-created customs, aerobic
        // goes under "aerobic" and anaerobic goes into a dedicated "custom" group.
        const presetEntry = PRESET_BY_KEY[e.exerciseKey]
        const groupKey: HomeGroupKey = presetEntry
          ? presetEntry.category
          : (custom.exerciseType === 'aerobic' ? 'aerobic' : 'custom')
        return {
          id: e.exerciseKey,
          exerciseKey: e.exerciseKey,
          label: custom.name,
          icon: custom.icon,
          color: (custom.exerciseType === 'aerobic' ? 'mint' : 'violet') as 'mint' | 'violet',
          path: pageLinks.CustomExercise(custom.exerciseKey),
          groupKey,
        }
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)

    // 7-day detail
    const monBj = new Date(bounds.weekStartUtc.getTime() + BJ_OFFSET_MS)
    const sunBj = new Date(bounds.weekStartUtc.getTime() + 6 * ONE_DAY_MS + BJ_OFFSET_MS)
    const weekLabel = `${monBj.getUTCMonth() + 1}/${monBj.getUTCDate()} — ${sunBj.getUTCMonth() + 1}/${sunBj.getUTCDate()}`

    const days = DAY_NAMES.map((dayName, i) => {
      const dayStartUtc = new Date(bounds.weekStartUtc.getTime() + i * ONE_DAY_MS)
      const dayEndUtc = new Date(dayStartUtc.getTime() + ONE_DAY_MS)
      const dayStartIso = dayStartUtc.toISOString()
      const dayEndIso = dayEndUtc.toISOString()
      const bjDayDate = new Date(dayStartUtc.getTime() + BJ_OFFSET_MS)
      const dateStr = `${bjDayDate.getUTCMonth() + 1}/${bjDayDate.getUTCDate()}`
      const sessions = weekSessions
        .filter(w => w.startedAt >= dayStartIso && w.startedAt < dayEndIso)
        .sort((a, b) => a.startedAt.localeCompare(b.startedAt))
        .map(w => {
          const startBj = new Date(new Date(w.startedAt).getTime() + BJ_OFFSET_MS)
          const hh = String(startBj.getUTCHours()).padStart(2, '0')
          const mm = String(startBj.getUTCMinutes()).padStart(2, '0')
          const custom = customByKey.get(w.exerciseKey)
          const label = custom?.name ?? w.exerciseKey
          const template: CustomExerciseTemplate | null = customTemplateByKey.get(w.exerciseKey) ?? null
          return {
            id: w.id,
            exerciseKey: w.exerciseKey,
            exerciseType: w.exerciseType,
            durationSeconds: w.durationSeconds,
            startTime: `${hh}:${mm}`,
            summary: w.summary,
            label,
            template,
          }
        })
      return {
        dayOfWeek: dayName, date: dateStr,
        isToday: i === bounds.todayIndex,
        isFuture: i > bounds.todayIndex,
        sessions,
      }
    })

    return {
      exercises: exercisesWithMeta,
      todayDate: bounds.todayDateStr,
      weeklyStats: { aerobicMinutes, anaerobic: calcAnaerobic(weekAnaerobic, customByKey) },
      todayStats: { aerobicMinutes: todayAerobicMinutes, anaerobic: calcAnaerobic(todayAnaerobic, customByKey) },
      weekLabel,
      days,
    }
  },
}))

// ============================================================================
// Derived atoms — unchanged public API for components
// ============================================================================

export const exercisesAtom = atom((get) => {
  const { data } = get(refreshAtom)
  return data?.exercises ?? []
})

export const todayDateAtom = atom((get) => {
  const { data } = get(refreshAtom)
  return data?.todayDate ?? ''
})

const defaultAnaerobic: AnaerobicDetail = { items: [] }

export const weeklyStatsAtom = atom((get) => {
  const { data } = get(refreshAtom)
  return data?.weeklyStats ?? { aerobicMinutes: 0, anaerobic: defaultAnaerobic }
})

export const todayStatsAtom = atom((get) => {
  const { data } = get(refreshAtom)
  return data?.todayStats ?? { aerobicMinutes: 0, anaerobic: defaultAnaerobic }
})

// Weekly detail atoms (also derived from refreshAtom now)
export const weeklyDetailAtom = refreshAtom

export const weekLabelAtom = atom((get) => {
  const { data } = get(refreshAtom)
  return data?.weekLabel ?? ''
})

export const daysAtom = atom((get) => {
  const { data } = get(refreshAtom)
  return data?.days ?? []
})

export function loader(store: Store) {
  store.get(refreshAtom)
  return null
}

// ============================================================================
// Monthly calendar — parameterised by 'YYYY-MM' in Beijing time
// ============================================================================

export interface MonthlyDay {
  /** 'YYYY-MM-DD' in Beijing time (or '' for leading/trailing empty cells) */
  dateStr: string
  /** Day of month (1-31), or 0 for empty cells */
  day: number
  /** True if dateStr is the BJ-current date */
  isToday: boolean
  /** True if dateStr > today (future) */
  isFuture: boolean
  /** True if this cell is not part of the displayed month (empty padding) */
  isPadding: boolean
  /** Aggregated session list for this date (in chronological order) */
  sessions: Array<{
    exerciseKey: string
    exerciseType: 'aerobic' | 'anaerobic'
    durationSeconds: number
    startTime: string  // HH:MM (BJ)
    summary: LocalSession['summary']
    label: string
    template: CustomExerciseTemplate | null
  }>
  /** Convenience: any aerobic session on this day? */
  hasAerobic: boolean
  /** Convenience: any anaerobic session on this day? */
  hasAnaerobic: boolean
}

export interface MonthlyStats {
  /** Days within this month that had at least one session */
  trainedDays: number
  /** Sum of aerobic durations across the month (minutes) */
  aerobicMinutes: number
  /** Total anaerobic sets across the month (sum of summary.totalSets) */
  anaerobicSets: number
  /** Total anaerobic reps across the month */
  anaerobicReps: number
}

export interface MonthlyData {
  /** First day of this month label, e.g. '2026 年 5 月' */
  monthLabel: string
  /** 'YYYY-MM' echo of the requested month */
  monthKey: string
  /** Always 6 weeks * 7 days = 42 cells, padded with isPadding cells */
  cells: MonthlyDay[]
  stats: MonthlyStats
}

function parseMonthKey(monthKey: string): { y: number; m0: number } {
  const [y, m] = monthKey.split('-').map(Number)
  return { y, m0: m - 1 }
}

/** Today's BJ date as 'YYYY-MM-DD'. */
function todayBjDateStr(): string {
  const bj = new Date(Date.now() + BJ_OFFSET_MS)
  const y = bj.getUTCFullYear()
  const m = String(bj.getUTCMonth() + 1).padStart(2, '0')
  const d = String(bj.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Build the BJ-midnight UTC instant for a given (year, monthIndex, day). */
function bjMidnightUtc(y: number, m0: number, d: number): Date {
  return new Date(Date.UTC(y, m0, d) - BJ_OFFSET_MS)
}

/** Map a BJ date in milliseconds (UTC) to 'YYYY-MM-DD' BJ string. */
function bjDateStr(utcMs: number): string {
  const bj = new Date(utcMs + BJ_OFFSET_MS)
  const y = bj.getUTCFullYear()
  const m = String(bj.getUTCMonth() + 1).padStart(2, '0')
  const d = String(bj.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export const monthlyStatsAtomFamily = atomFamily((monthKey: string) =>
  atomWithQuery(() => ({
    queryKey: ['HomeStore', 'monthly', monthKey],
    queryFn: async (): Promise<MonthlyData> => {
      const { y, m0 } = parseMonthKey(monthKey)
      const monthLabel = `${y} 年 ${m0 + 1} 月`
      const today = todayBjDateStr()

      const monthStartUtc = bjMidnightUtc(y, m0, 1)
      const monthEndUtc = bjMidnightUtc(y, m0 + 1, 1)
      const monthStartIso = monthStartUtc.toISOString()
      const monthEndIso = monthEndUtc.toISOString()

      const [allSessionsRaw, allCustoms] = await Promise.all([
        db.sessions.where('startedAt').between(monthStartIso, monthEndIso, true, false).toArray(),
        db.customExercises.toArray(),
      ])
      const allSessions = allSessionsRaw.filter(s => !s.pendingDelete)
      const customByKey = new Map(allCustoms.map(c => [c.exerciseKey, c]))

      // Group sessions by BJ date
      const byDate = new Map<string, MonthlyDay['sessions']>()
      for (const s of allSessions) {
        const startedMs = new Date(s.startedAt).getTime()
        const dateStr = bjDateStr(startedMs)
        const startBj = new Date(startedMs + BJ_OFFSET_MS)
        const hh = String(startBj.getUTCHours()).padStart(2, '0')
        const mm = String(startBj.getUTCMinutes()).padStart(2, '0')
        const custom = customByKey.get(s.exerciseKey)
        const entry = {
          exerciseKey: s.exerciseKey,
          exerciseType: s.exerciseType,
          durationSeconds: s.durationSeconds,
          startTime: `${hh}:${mm}`,
          summary: s.summary,
          label: custom?.name ?? s.exerciseKey,
          template: custom?.template ?? null,
        }
        const arr = byDate.get(dateStr) ?? []
        arr.push(entry)
        byDate.set(dateStr, arr)
      }
      for (const list of byDate.values()) {
        list.sort((a, b) => a.startTime.localeCompare(b.startTime))
      }

      // Determine first cell: Monday on or before day 1 of the month
      const firstDow = new Date(monthStartUtc.getTime() + BJ_OFFSET_MS).getUTCDay() // 0=Sun
      const mondayOffset = firstDow === 0 ? -6 : 1 - firstDow
      const gridStartUtc = new Date(monthStartUtc.getTime() + mondayOffset * ONE_DAY_MS)

      // Dynamic row count: include only as many weeks as the month spans.
      // leading padding + days in month + trailing padding, rounded up to whole weeks.
      const daysInMonth = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate()
      const leading = -mondayOffset            // 0..6 days before day 1
      const tailRemainder = (leading + daysInMonth) % 7
      const trailing = tailRemainder === 0 ? 0 : 7 - tailRemainder
      const totalCells = leading + daysInMonth + trailing

      const cells: MonthlyDay[] = []
      for (let i = 0; i < totalCells; i++) {
        const cellUtcMs = gridStartUtc.getTime() + i * ONE_DAY_MS
        const dateStr = bjDateStr(cellUtcMs)
        const bj = new Date(cellUtcMs + BJ_OFFSET_MS)
        const cellMonth0 = bj.getUTCMonth()
        const cellDay = bj.getUTCDate()
        const isPadding = cellMonth0 !== m0
        const sessions = byDate.get(dateStr) ?? []
        cells.push({
          dateStr: isPadding ? '' : dateStr,
          day: isPadding ? 0 : cellDay,
          isToday: !isPadding && dateStr === today,
          isFuture: !isPadding && dateStr > today,
          isPadding,
          sessions: isPadding ? [] : sessions,
          hasAerobic: !isPadding && sessions.some(x => x.exerciseType === 'aerobic'),
          hasAnaerobic: !isPadding && sessions.some(x => x.exerciseType === 'anaerobic'),
        })
      }

      // Monthly stats from in-month cells
      let trainedDays = 0
      let aerobicMinutes = 0
      let anaerobicSets = 0
      let anaerobicReps = 0
      for (const c of cells) {
        if (c.isPadding) continue
        if (c.sessions.length > 0) trainedDays++
        for (const s of c.sessions) {
          if (s.exerciseType === 'aerobic') {
            aerobicMinutes += Math.round(s.durationSeconds / 60)
          } else {
            const summary = (s.summary ?? {}) as Record<string, unknown>
            anaerobicSets += Number(summary.totalSets ?? 0)
            anaerobicReps += Number(summary.totalReps ?? 0)
          }
        }
      }

      return {
        monthLabel,
        monthKey,
        cells,
        stats: { trainedDays, aerobicMinutes, anaerobicSets, anaerobicReps },
      }
    },
  }))
)
